import React, { Fragment } from 'react';

import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import commonMessages from '~/core/commonMessages';
import { CountryCode, PageName } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { PlayoffSeriesInfo } from '~/core/types';
import { FormatPlayoffSeriesInfo, isValidLinkScheduleState } from '~/core/utils';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import { GameLinksPopper } from '~/views/components/gameLinksPopper/GameLinksPopper';
import TvBroadcast from '~/views/components/tvBroadcast/TvBroadcast';
import {
    SeriesStatusMessage,
    SIZES as PLAYOFF_SIZES,
} from '~/views/pages/playoffSeries/components/PlayoffSeriesTable/styles';
import { PlayoffSeriesGame, PlayoffSeriesTeam } from '~/views/pages/playoffSeries/types';
import AlternateBroadcasts from '~/views/pages/schedule/components/listView/components/alternateBroadcasts/AlternateBroadcasts';
import { gameLinksHeaderStyle } from '~/views/pages/schedule/components/listView/components/dayOverview/columnStyles';
import { SIZES as SCHEDULE_SIZES } from '~/views/pages/schedule/components/listView/components/dayOverview/styles';
import { ScheduleGame } from '~/views/pages/schedule/types';

import { gameLinksColumnStyle, networksColumnStyle, networksStyle } from './columnStyles';
import { getEventType, getPartnerLinks, useLinksWithRecap } from './utils';

import {
    LargeGameLink,
    LargeGameLinksContainer,
    LineBreak,
    LinkTag,
    SHARED_SIZES,
    SmallGameLinksContainer,
} from './styles';

// table columns are either used on the schedule listView page, or the playoff series page
const columnHelper = createColumnHelper<ScheduleGame | PlayoffSeriesGame>();
export const networkColumn = (
    location: CountryCode,
    doNotSortNetworks: boolean,
    pageName: PageName,
    bottomSeedTeam: PlayoffSeriesTeam | null = null,
    topSeedTeam: PlayoffSeriesTeam | null = null,
    neededToWin: number | null = null
): ColumnDef<PlayoffSeriesGame | ScheduleGame> => {
    const isPlayoffSeriesPage = pageName === PageName.PlayoffSeries;

    return columnHelper.display({
        id: 'networks',
        size: 240,
        header: isPlayoffSeriesPage
            ? ''
            : () => <FormattedMessage {...commonMessages.gameInformationDescriptions.broadcasters} />,
        cell: (data) => {
            const {
                gameState,
                seriesStatus: { bottomSeedWins, topSeedWins } = { bottomSeedWins: undefined, topSeedWins: undefined },
            } = data.row.original;

            if (
                isPlayoffSeriesPage &&
                CoreUtils.isPostGame(gameState) &&
                topSeedWins != undefined &&
                bottomSeedWins != undefined
            ) {
                const playoffSeriesInfo: PlayoffSeriesInfo = {
                    topSeedAbbrev: (topSeedTeam as PlayoffSeriesTeam).abbrev,
                    topSeedWins: topSeedWins,
                    bottomSeedAbbrev: (bottomSeedTeam as PlayoffSeriesTeam).abbrev,
                    bottomSeedWins: bottomSeedWins,
                    neededToWin: neededToWin || null,
                };

                return (
                    <SeriesStatusMessage>
                        <FormatPlayoffSeriesInfo playoffSeriesInfo={playoffSeriesInfo} />
                    </SeriesStatusMessage>
                );
            }

            return (
                <div>
                    <TvBroadcast
                        doNotSortNetworks={doNotSortNetworks}
                        location={location}
                        pageName={pageName}
                        tvBroadcasts={data.row.original.tvBroadcasts}
                    />
                    {data.row.original.alternateBroadcasts !== undefined && (
                        <AlternateBroadcasts broadcasts={data.row.original.alternateBroadcasts} location={location} />
                    )}
                </div>
            );
        },
        meta: {
            className: isPlayoffSeriesPage ? 'playoffSeriesColumn' : 'scheduleColumn networksColumn',
            headerClassName: isPlayoffSeriesPage ? '' : 'scheduleHeader networksColumnHeader',
            style: networksColumnStyle(isPlayoffSeriesPage),
            headerStyle: networksStyle(isPlayoffSeriesPage),
            size: SHARED_SIZES.network.size,
        },
    });
};

type GameLinksProp = {
    countryCode: CountryCode;
    intl: IntlShape;
    pageName: PageName;
    showOnlyHomeTickets: boolean;
    teamTriCode?: string;
};

export const gameLinksColumn = ({
    countryCode,
    intl,
    pageName,
    showOnlyHomeTickets,
    teamTriCode,
}: GameLinksProp): ColumnDef<PlayoffSeriesGame | ScheduleGame> => {
    const SIZES = pageName === PageName.PlayoffSeries ? PLAYOFF_SIZES.gameLinks : SCHEDULE_SIZES.gameLinks;
    return columnHelper.display({
        id: 'gameLinks',
        minSize: SIZES.minSize,
        size: SIZES.size,
        maxSize: SIZES.maxSize,
        header: '',
        cell: function CellComponent(data) {
            const isWebview = useIsWebview();
            const intl = useIntl();

            const isPlayoffSeriesPage = pageName === PageName.PlayoffSeries;
            const game = data.row.original;

            const { data: streamUrl } = useGetWebStreamURLQuery(
                {
                    broadcasts: game.tvBroadcasts,
                    countryCode,
                    language: intl.locale as ShortCodeEnums.Language,
                },
                { skip: countryCode === CountryCode.None }
            );

            const ticketsLink = useOptionForLocale({
                [ShortCodeEnums.Language.English]: game.ticketsLink,
                [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
            });

            const gameLinks = useLinksWithRecap({
                game,
                isWebview,
                pageName,
                showOnlyHomeTickets,
                ticketsLink,
                streamUrl,
                teamTriCode,
            });

            const partnerLinks =
                isValidLinkScheduleState(game.gameScheduleState) && pageName === PageName.ClubSchedule
                    ? getPartnerLinks(game as ScheduleGame, game.homeTeam.abbrev.toLowerCase() === teamTriCode)
                    : [];

            const eventType = getEventType(game as ScheduleGame);

            return (
                <>
                    <SmallGameLinksContainer $eventType={eventType}>
                        <GameLinksPopper
                            game={game}
                            gameLinks={gameLinks.concat(partnerLinks)}
                            isPlayoffSeriesPage={isPlayoffSeriesPage}
                        />
                    </SmallGameLinksContainer>
                    <LargeGameLinksContainer>
                        {gameLinks.map((gameLink) => (
                            <Fragment key={gameLink.actionUrl}>
                                <LargeGameLink>
                                    {gameLink.isVideoLink && gameLink.icon}
                                    <LinkTag href={gameLink.actionUrl} $isPlayoffSeriesPage={isPlayoffSeriesPage}>
                                        {gameLink.message}
                                    </LinkTag>
                                </LargeGameLink>
                                <LineBreak $extraSpace />
                            </Fragment>
                        ))}
                        {partnerLinks?.length >= 1 && (
                            <GameLinksPopper
                                game={game}
                                gameLinks={partnerLinks}
                                isPlayoffSeriesPage={isPlayoffSeriesPage}
                            />
                        )}
                    </LargeGameLinksContainer>
                </>
            );
        },
        meta: {
            className: `scheduleColumn gameLinksColumn`,
            headerAriaLabel: () => intl.formatMessage(commonMessages.ariaLabels.links.gameLinks),
            headerClassName: 'scheduleHeader',
            headerStyle: gameLinksHeaderStyle,
            style: (theme) => gameLinksColumnStyle(theme, pageName),
        },
    });
};
